import React, { useCallback, useMemo, type RefObject, type ReactNode, useRef } from 'react';
import groupBy from 'lodash/groupBy';
import noop from 'lodash/noop';
import type { OptionType, OptionProps } from '@atlaskit/select';
import type { GroupBase, OptionsOrGroups } from '@atlaskit/react-select';
import type { AtlaskitSelectRefType } from '@atlaskit/select/types';
import { Box, xcss, Flex, Pressable } from '@atlaskit/primitives';
import {
	GIC_VIEW_SOURCE,
	GIC_VIEW_NO_RESULTS_SOURCE,
} from '@atlassian/jira-issue-type-config-common/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	EditIssueTypeUFOExperience,
	CreateIssueTypeUFOExperience,
} from '@atlassian/jira-issue-create-analytics/src/services/experiences/index.tsx';
import type {
	IssueType,
	RequestType,
	RequestTypePractices,
	IssueOrRequestType,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { GroupComponentWithStickyFooter } from '@atlassian/jira-select-with-footer-options/src/GroupComponentWithStickyFooter.tsx';
import {
	SERVICE_REQUEST,
	CHANGE_MANAGEMENT,
	PROBLEM_MANAGEMENT,
	INCIDENT_MANAGEMENT,
	POST_INCIDENT_REVIEW,
	DEVELOPER_ESCALATION,
	ISSUE_TYPE,
	REQUEST_TYPE,
	UNASSIGNED_REQUEST_TYPE,
} from '@atlassian/jira-issue-create-common-types/src/common/constants/graphql-field-types.tsx';

import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	useAppendStickyFooterOptions,
	type FooterOption,
} from '@atlassian/jira-select-with-footer-options/src/utils.tsx';
import { OptionWithButtonStyle } from '@atlassian/jira-select-with-footer-options/src/OptionWithButtonStyle.tsx';
import { OptionWithAutoKeyboardScroll } from '@atlassian/jira-select-with-footer-options/src/OptionWithAutoKeyboardScroll.tsx';
import { asyncCreateIssueTypeModalEntryPoint } from '@atlassian/jira-create-issue-type-modal/entrypoint.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { defaultModalProps } from '@atlassian/jira-issue-type-config-common/src/constants.tsx';
import { FormDialogSkeleton } from '@atlassian/jira-form-dialog-skeleton/src/ui/FormDialogSkeleton.tsx';
import { Trigger, useTriggerContext } from '@atlassian/entry-point-panel';
import { asyncEditIssueTypeModalEntrypoint } from '@atlassian/jira-edit-issue-type-modal/entrypoint.tsx';
import {
	OPTION_TYPE_CREATE_ISSUE_TYPE,
	OPTION_TYPE_EDIT_ISSUE_TYPE,
	OPTION_TYPE_MANAGE_ISSUE_TYPE,
	REFOCUS_DELAY_MS,
} from './constants.tsx';
import type {
	RequestTypeOption,
	RequestAndIssueTypeOptions,
	NoneOption,
} from './request-type-select/types.tsx';
import type {
	CustomOptionQueryParams,
	CustomOptionsAndComponentsForInlineIssueTypesProps,
} from './types.tsx';
import messages from './messages.tsx';
import { CreateWorkTypeButton } from './configure-work-type/create-work-type-button/CreateWorkTypeButton.tsx';
import { ConfigureWorkTypePanel } from './configure-work-type/ConfigureWorkTypePanel.tsx';

export const getFieldLabel = (hasRequestTypes: boolean, formatMessage: FormatMessage): string =>
	hasRequestTypes
		? formatMessage(messages.typeFieldLabel)
		: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.issueTypeFieldLabelIssueTermRefresh
					: messages.issueTypeFieldLabel,
			);

export const getRequestTypeGroupLabel = (
	practice: RequestTypePractices,
	formatMessage: FormatMessage,
): string => {
	if (practice === CHANGE_MANAGEMENT) {
		return formatMessage(messages.changeRequestGroupLabel);
	}
	if (practice === INCIDENT_MANAGEMENT) {
		return formatMessage(messages.incidentRequestGroupLabel);
	}
	if (practice === PROBLEM_MANAGEMENT) {
		return formatMessage(messages.problemRequestGroupLabel);
	}
	if (practice === SERVICE_REQUEST) {
		return formatMessage(messages.serviceRequestGroupLabel);
	}
	if (practice === POST_INCIDENT_REVIEW) {
		return formatMessage(messages.postIncidentReviewRequestGroupLabel);
	}
	if (practice === DEVELOPER_ESCALATION) {
		return formatMessage(messages.developerEscalationRequestGroupLabel);
	}
	if (practice === UNASSIGNED_REQUEST_TYPE) {
		return formatMessage(messages.unassignedRequestTypeGroupLabel);
	}

	return formatMessage(messages.requestTypeGroupLabel);
};

const reorderRequestTypeGroups = (
	requestTypeGroupKeys: RequestTypePractices[],
): RequestTypePractices[] => {
	// This is the order we have them for queues, request types and feature page
	const allPractices: RequestTypePractices[] = [
		SERVICE_REQUEST,
		INCIDENT_MANAGEMENT,
		PROBLEM_MANAGEMENT,
		CHANGE_MANAGEMENT,
		POST_INCIDENT_REVIEW,
		DEVELOPER_ESCALATION,
		UNASSIGNED_REQUEST_TYPE,
		REQUEST_TYPE,
	];
	const groupKeySet = new Set(requestTypeGroupKeys);
	return allPractices.filter((practice) => groupKeySet.has(practice));
};

const createNoneOption = (formatMessage: FormatMessage): NoneOption => ({
	label: formatMessage(messages.noRequestTypeLabel),
	value: 'none',
	type: 'NONE',
});

export const getRequestAndIssueTypeOptionsGroup = (
	issueTypes: IssueType[],
	requestTypes: RequestType[],
	formatMessage: FormatMessage,
): RequestAndIssueTypeOptions => {
	// @ts-expect-error(PARTIAL_RECORD) TS2322 | TS2322 - Type 'Dictionary<RequestType[]>' is not assignable to type 'Record<RequestTypePractices, RequestType[]>'. | Type 'Dictionary<RequestType[]>' is not assignable to type 'Record<RequestTypePractices, RequestType[]>'.
	const groupedRequestTypes: Record<RequestTypePractices, RequestType[]> = groupBy(
		requestTypes,
		(o) => o.practice,
	);

	// @ts-expect-error - TS2345 - Argument of type 'string[]' is not assignable to parameter of type 'RequestTypePractices[]'.
	const groupKeys = reorderRequestTypeGroups(Object.keys(groupedRequestTypes));
	const requestTypesOptions: RequestTypeOption[] = groupKeys.map((practice) => ({
		label: getRequestTypeGroupLabel(practice, formatMessage),
		options: groupedRequestTypes[practice],
	}));

	return {
		issueTypeOptions: issueTypes,
		requestTypeOptions: [createNoneOption(formatMessage), ...requestTypesOptions],
	};
};

export const getIssueTypeAndRequestType = ({
	requestOrIssueType,
	allIssueTypes,
	formatMessage,
}: {
	requestOrIssueType: IssueOrRequestType;
	allIssueTypes: IssueType[];
	formatMessage: FormatMessage;
}): {
	issueType: IssueType | null;
	requestType: RequestType | NoneOption | null;
} => {
	const noneOption = createNoneOption(formatMessage);

	let issueType: IssueType | null = null;
	let requestType: RequestType | NoneOption | null = null;

	if (requestOrIssueType !== null) {
		switch (requestOrIssueType.type) {
			case ISSUE_TYPE:
				issueType = requestOrIssueType;
				requestType = noneOption;
				break;
			case REQUEST_TYPE:
				issueType =
					allIssueTypes.find(({ value }) => value === requestOrIssueType.issueTypeId) || null;
				requestType = requestOrIssueType;
				break;
			default: // default case forced by flow
		}
	}

	return { issueType, requestType };
};

export const getFirstMatchingRequestType = ({
	issueType,
	allRequestTypes,
}: {
	issueType: IssueType;
	allRequestTypes: RequestType[];
}): RequestType | undefined =>
	allRequestTypes.find(({ issueTypeId }) => issueTypeId === issueType.value);

export const useOptionsWithFooterActions = (
	targetOptions: OptionType[],
): OptionsOrGroups<OptionType, GroupBase<OptionType>> => {
	const { formatMessage } = useIntl();
	const createIssueOrWorkTypeMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.addWorkTypeButtonText
		: messages.addIssueTypeButtonText;

	const editIssueOrWorkTypeMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.editWorkTypeButtonTextIssueTermRefresh
		: messages.editIssueTypeButtonText;

	const manageIssueOrWorkTypesMessage = fg('jira-issue-terminology-refresh-m3')
		? messages.manageWorkTypesButtonTextIssueTermRefresh
		: messages.manageIssueTypesButtonText;

	const footerOptions: FooterOption[] = useMemo(() => {
		return [
			{
				label: formatMessage(createIssueOrWorkTypeMessage),
				type: OPTION_TYPE_CREATE_ISSUE_TYPE,
				value: OPTION_TYPE_CREATE_ISSUE_TYPE,
			},
			{
				label: formatMessage(editIssueOrWorkTypeMessage),
				type: OPTION_TYPE_EDIT_ISSUE_TYPE,
				value: OPTION_TYPE_EDIT_ISSUE_TYPE,
			},
			{
				label: formatMessage(manageIssueOrWorkTypesMessage),
				type: OPTION_TYPE_MANAGE_ISSUE_TYPE,
				value: OPTION_TYPE_MANAGE_ISSUE_TYPE,
			},
		];
	}, [
		formatMessage,
		createIssueOrWorkTypeMessage,
		editIssueOrWorkTypeMessage,
		manageIssueOrWorkTypesMessage,
	]);

	return useAppendStickyFooterOptions(targetOptions, footerOptions);
};

export const useCustomOptionForWorkTypeConfig = ({ ...queryParams }: CustomOptionQueryParams) => {
	const createPopupTrigger = useTriggerContext(ConfigureWorkTypePanel);
	const editPopupTrigger = useTriggerContext(ConfigureWorkTypePanel);

	return useMemo(() => {
		const CustomOptionForWorkTypeConfig = ({ children, ...props }: OptionProps<OptionType>) => {
			if (OPTION_TYPE_MANAGE_ISSUE_TYPE === props.data.type) {
				return (
					<OptionWithButtonStyle
						testId={`issue-create.ui.modal.create-form.type-picker.option-with-button-${props.data.type}`}
						{...props}
						innerProps={{
							...props.innerProps,
							role: fg('jjj-a11y-listbox-should-not-have-nested-button-gic') ? 'option' : 'button',
						}}
					>
						{children}
					</OptionWithButtonStyle>
				);
			}

			const OptionWrapper = fg('jjj-a11y-listbox-should-not-have-nested-button-gic')
				? Box
				: Pressable;

			if (OPTION_TYPE_CREATE_ISSUE_TYPE === props.data.type) {
				const {
					createEntryPointProps,
					createEntryPointParams,
					createIssueTypeButtonRef,
					shouldTriggerModal,
					onClickCreateOrEditOption,
				} = queryParams;

				const getClickElement = (ref: React.RefObject<HTMLElement>) => (
					<OptionWrapper
						xcss={pressableStyles}
						// onClick is intercepted by ModalEntryPointPressableTrigger/Trigger component via usePressablePreloadRef
						// so we need to manually trigger a synthetic click on left mouse click
						onMouseDown={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
							// Left mouse button was clicked
							if (e.button === 0) {
								createIssueTypeButtonRef?.current?.click();
							}
						}}
						onClick={() => onClickCreateOrEditOption('createIssueTypeButton')}
						// ref returned from entrypoint is of Type 'RefObject<HTMLElement>' is not assignable to type 'Ref<HTMLButtonElement>
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						ref={ref as React.Ref<HTMLButtonElement>}
					>
						<OptionWithButtonStyle
							testId={`issue-create.ui.modal.create-form.type-picker.option-with-button-${props.data.type}`}
							{...props}
						>
							{children}
						</OptionWithButtonStyle>
					</OptionWrapper>
				);

				// eslint-disable-next-line no-nested-ternary
				return shouldTriggerModal ? (
					fg('inline_config_migrate_deprecated_modal_entrypoints') ? (
						<ModalTrigger
							entryPoint={asyncCreateIssueTypeModalEntryPoint}
							entryPointProps={createEntryPointProps}
							entryPointParams={createEntryPointParams}
							modalProps={defaultModalProps}
							Fallback={() => <FormDialogSkeleton numFormFields={1} />}
							forwardedRef={createIssueTypeButtonRef}
						>
							{({ ref }) => getClickElement(ref)}
						</ModalTrigger>
					) : (
						<ModalEntryPointPressableTrigger
							entryPoint={asyncCreateIssueTypeModalEntryPoint}
							entryPointProps={createEntryPointProps}
							entryPointParams={createEntryPointParams}
							interactionName="create-issue-type-modal"
							modalProps={defaultModalProps}
							useInternalModal
							forwardedRef={createIssueTypeButtonRef}
							Fallback={() => <FormDialogSkeleton numFormFields={1} />}
						>
							{({ ref }) => getClickElement(ref)}
						</ModalEntryPointPressableTrigger>
					)
				) : (
					<Trigger
						trigger={createPopupTrigger}
						entryPoint={asyncCreateIssueTypeModalEntryPoint}
						entryPointProps={createEntryPointProps}
						entryPointParams={createEntryPointParams}
						fallback={<FormDialogSkeleton numFormFields={1} />}
						forwardedRef={createIssueTypeButtonRef}
					>
						{({ ref }) => getClickElement(ref)}
					</Trigger>
				);
			}

			if (OPTION_TYPE_EDIT_ISSUE_TYPE === props.data.type) {
				const {
					editIssueTypeButtonRef,
					editEntryPointProps,
					editEntryPointParams,
					shouldTriggerModal,
					onClickCreateOrEditOption,
				} = queryParams;

				const getClickElement = (ref: React.RefObject<HTMLElement>) => (
					<OptionWrapper
						xcss={pressableStyles}
						// onClick is intercepted by ModalEntryPointPressableTrigger/Trigger component via usePressablePreloadRef,
						// so we need to manually trigger a synthetic click on left mouse click
						onMouseDown={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
							// Left mouse button was clicked
							if (e.button === 0) {
								editIssueTypeButtonRef?.current?.click();
							}
						}}
						onClick={() => onClickCreateOrEditOption('editIssueTypeButton')}
						// ref returned from entrypoint is of Type 'RefObject<HTMLElement>' is not assignable to type 'Ref<HTMLButtonElement>
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						ref={ref as React.Ref<HTMLButtonElement>}
					>
						<OptionWithButtonStyle
							testId={`issue-create.ui.modal.create-form.type-picker.option-with-button-${props.data.type}`}
							{...props}
						>
							{children}
						</OptionWithButtonStyle>
					</OptionWrapper>
				);

				// eslint-disable-next-line no-nested-ternary
				return shouldTriggerModal ? (
					fg('inline_config_migrate_deprecated_modal_entrypoints') ? (
						<ModalTrigger
							entryPoint={asyncEditIssueTypeModalEntrypoint}
							entryPointProps={editEntryPointProps}
							entryPointParams={editEntryPointParams}
							modalProps={defaultModalProps}
							Fallback={() => <FormDialogSkeleton numFormFields={3} />}
							forwardedRef={editIssueTypeButtonRef}
						>
							{({ ref }) => getClickElement(ref)}
						</ModalTrigger>
					) : (
						<ModalEntryPointPressableTrigger
							entryPoint={asyncEditIssueTypeModalEntrypoint}
							entryPointProps={editEntryPointProps}
							entryPointParams={editEntryPointParams}
							interactionName="create-issue-type-modal"
							modalProps={defaultModalProps}
							useInternalModal
							Fallback={() => <FormDialogSkeleton numFormFields={3} />}
							forwardedRef={editIssueTypeButtonRef}
						>
							{({ ref }) => getClickElement(ref)}
						</ModalEntryPointPressableTrigger>
					)
				) : (
					<Trigger
						trigger={editPopupTrigger}
						entryPoint={asyncEditIssueTypeModalEntrypoint}
						entryPointProps={editEntryPointProps}
						entryPointParams={editEntryPointParams}
						fallback={<FormDialogSkeleton numFormFields={3} />}
						forwardedRef={editIssueTypeButtonRef}
					>
						{({ ref }) => getClickElement(ref)}
					</Trigger>
				);
			}

			return (
				<OptionWithAutoKeyboardScroll {...props}>
					<Flex xcss={optionContainerStyles}>{children}</Flex>
				</OptionWithAutoKeyboardScroll>
			);
		};
		return CustomOptionForWorkTypeConfig;
	}, [queryParams, createPopupTrigger, editPopupTrigger]);
};

export const useCustomOptionsAndComponentsForWorkTypeConfig = ({
	options,
	projectId,
	projectKey,
	issueTypeId,
	canConfigureIssueTypes,
	onIssueTypeCreatedOrUpdated = noop,
	onCreateModalClose,
	onEditModalClose,
	createIssueTypeButtonRef,
	editIssueTypeButtonRef,
	shouldTriggerModal,
	onClickCreateOrEditOption,
}: CustomOptionsAndComponentsForInlineIssueTypesProps) => {
	const noOptionWrapperRef = useRef<HTMLElement>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const onCloseCreateIssueTypeModal = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				actionSubject: 'inlineCreateIssueTypeModal',
				action: 'closed',
			}),
		);
		onCreateModalClose();
	}, [createAnalyticsEvent, onCreateModalClose]);

	const createEntryPointParams = useMemo(
		() => ({
			cloudId,
			projectKey,
			shouldFetchProjectData: true,
		}),
		[cloudId, projectKey],
	);

	const createEntryPointProps = useMemo(
		() => ({
			onIssueTypeCreated: onIssueTypeCreatedOrUpdated,
			issueTypesCountLimit: 30,
			onClose: onCloseCreateIssueTypeModal,
			onOpen: noop,
			source: GIC_VIEW_SOURCE,
			ufoExperience: CreateIssueTypeUFOExperience,
			showNewIssueTypeForm: true,
			isWrappedInModal: shouldTriggerModal,
		}),
		[onIssueTypeCreatedOrUpdated, onCloseCreateIssueTypeModal, shouldTriggerModal],
	);

	const editEntryPointParams = useMemo(
		() => ({
			cloudId,
			projectKey,
		}),
		[cloudId, projectKey],
	);

	const editEntryPointProps = useMemo(
		() => ({
			onRefetchIssueTypesWhenIssueTypeUpdated: onIssueTypeCreatedOrUpdated,
			onClose: onEditModalClose,
			onOpen: noop,
			calledFrom: GIC_VIEW_SOURCE,
			ufoExperience: EditIssueTypeUFOExperience,
			shouldShowIssueTypeSelect: true,
			issueTypeId,
			projectId: Number(projectId),
			projectKey,
			relevantIssueTypeIds: options.map((option) => option.value.toString()),
			isWrappedInModal: shouldTriggerModal,
		}),
		[
			onIssueTypeCreatedOrUpdated,
			onEditModalClose,
			issueTypeId,
			projectId,
			projectKey,
			options,
			shouldTriggerModal,
		],
	);

	const CustomOptionWithInlineConfig = useCustomOptionForWorkTypeConfig({
		projectId: Number(projectId),
		ufoExperience: EditIssueTypeUFOExperience,
		onRefetchIssueTypesWhenIssueTypeUpdated: onIssueTypeCreatedOrUpdated,
		calledFrom: GIC_VIEW_SOURCE,
		onEditModalClose,
		projectKey,
		issueTypeId,
		createIssueTypeButtonRef,
		editIssueTypeButtonRef,
		createEntryPointProps,
		createEntryPointParams,
		editEntryPointProps,
		editEntryPointParams,
		shouldTriggerModal,
		onClickCreateOrEditOption,
	});

	const optionsWithActions = useOptionsWithFooterActions(options ?? []);

	const createEntryPointPropsForNoOptions = useMemo(
		() => ({
			...createEntryPointProps,
			source: GIC_VIEW_NO_RESULTS_SOURCE,
		}),
		[createEntryPointProps],
	);

	const handleClick = useCallback(() => {
		onClickCreateOrEditOption('createIssueTypeButton');
	}, [onClickCreateOrEditOption]);

	if (!canConfigureIssueTypes) {
		return {
			options,
			components: undefined,
		};
	}

	const NoOptionsMessage = () => {
		// eslint-disable-next-line react-hooks/react-compiler
		const { formatMessage } = useIntl();
		return (
			<Box ref={noOptionWrapperRef} paddingInline="space.150" paddingBlock="space.050">
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.noSearchResultsIssueTermRefresh
						: messages.noSearchResultsOldTerm,
					{
						button: (chunks: ReactNode) => (
							<CreateWorkTypeButton
								onClick={handleClick}
								entryPointProps={createEntryPointPropsForNoOptions}
								entryPointParams={createEntryPointParams}
								shouldTriggerModal={shouldTriggerModal}
							>
								{chunks}
							</CreateWorkTypeButton>
						),
					},
				)}
			</Box>
		);
	};

	return {
		options: optionsWithActions,
		components: {
			Group: GroupComponentWithStickyFooter,
			Option: CustomOptionWithInlineConfig,
			NoOptionsMessage,
		},
		noOptionWrapperRef,
	};
};

export const useRefocusSelectField = (selectRef: RefObject<AtlaskitSelectRefType>) => {
	return () => {
		// When Create issue type or Edit issue type modal is closed, the modal will automatically return focus to the issue type field
		// The returned focus is returning fine and works well for keyboard users, however, @atlaskit/select caused some weird bug which
		// made issue type field not clickable for mouse users until it gets a refocus after a while, if we refocus immediately it also won't work
		// We tried to refocus with requestIdleCallback and requestAnimationFrame to delay the focus point based on CPU Idle and frame Idle
		// but none of that works so we need to use setTimeout temporarily before ADS team fix this issue
		setTimeout(() => {
			// Refocusing to make issue type field clickable after focus is returned from closing modal
			selectRef.current?.blur();
			selectRef.current?.focus();
		}, REFOCUS_DELAY_MS);
	};
};

const optionContainerStyles = xcss({
	wordBreak: 'break-word',
});

const pressableStyles = xcss({
	height: '100%',
	width: '100%',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	fontWeight: 'font.weight.regular',
	padding: 'space.0',
	font: 'font.body',
});
